import { PassClaims } from '@stoaria-control/schema'
import { b64u, bufferSource, unb64u, utf8 } from './encoding.ts'

// A pass is `spass.<b64url(canonical JSON claims)>.<b64url(Ed25519 sig)>`.
// It is a signed claim set, not X.509: Cloudflare terminates TLS, so the
// signature — checked against the daemon's registered public key — is the
// only thing the API trusts.
export const PASS_PREFIX = 'spass.'
const subtle = globalThis.crypto.subtle
const ALG = { name: 'Ed25519' } as const
// 30 s of clock skew between node01 and the Worker is tolerated.
const SKEW_SECONDS = 30

export async function generateSigningKeyPair(): Promise<{
  publicKey: Uint8Array
  privateKey: Uint8Array
}> {
  const kp = await subtle.generateKey(ALG, true, ['sign', 'verify'])
  return {
    publicKey: new Uint8Array(await subtle.exportKey('raw', kp.publicKey)),
    privateKey: new Uint8Array(await subtle.exportKey('pkcs8', kp.privateKey)),
  }
}

// Canonical: keys sorted, so the same claims always sign identically.
function canonical(claims: PassClaims): string {
  const sorted = Object.fromEntries(
    Object.entries(claims).sort(([a], [b]) => a.localeCompare(b)),
  )
  return JSON.stringify(sorted)
}

export async function signPass(
  claims: PassClaims,
  privateKeyPkcs8: Uint8Array,
): Promise<string> {
  const key = await subtle.importKey(
    'pkcs8',
    bufferSource(privateKeyPkcs8),
    ALG,
    false,
    ['sign'],
  )
  const body = bufferSource(utf8(canonical(PassClaims.parse(claims))))
  const sig = new Uint8Array(await subtle.sign(ALG, key, body))
  return `${PASS_PREFIX}${b64u(body)}.${b64u(sig)}`
}

// Splits `spass.<body>.<sig>` into its two decoded parts. A token whose
// segments are not base64url (or whose shape is wrong) is not a pass at all,
// so decode failures are reported as null rather than thrown.
function decodeToken(
  token: string,
): { body: Uint8Array; sig: Uint8Array } | null {
  if (!token.startsWith(PASS_PREFIX)) return null
  const [bodyText, sigText, extra] = token.slice(PASS_PREFIX.length).split('.')
  if (!bodyText || !sigText || extra !== undefined) return null
  try {
    return { body: unb64u(bodyText), sig: unb64u(sigText) }
  } catch {
    // Malformed base64url → "not a pass", the caller's null path.
    return null
  }
}

export async function verifyPass(
  token: string,
  publicKeyRaw: Uint8Array,
  now: number = Math.floor(Date.now() / 1000),
): Promise<PassClaims | null> {
  const decoded = decodeToken(token)
  if (decoded === null) return null
  const { body, sig } = decoded
  const key = await subtle.importKey(
    'raw',
    bufferSource(publicKeyRaw),
    ALG,
    false,
    ['verify'],
  )
  const valid = await subtle.verify(
    ALG,
    key,
    bufferSource(sig),
    bufferSource(body),
  )
  if (!valid) return null
  const parsed = PassClaims.safeParse(
    JSON.parse(new TextDecoder().decode(body)),
  )
  if (!parsed.success) return null
  const { iat, exp } = parsed.data
  if (exp <= now || iat > now + SKEW_SECONDS) return null
  return parsed.data
}

export async function argvHash(argv: string[]): Promise<string> {
  const digest = await subtle.digest(
    'SHA-256',
    bufferSource(utf8(JSON.stringify(argv))),
  )
  return Array.from(new Uint8Array(digest), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('')
}
